import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import './UserStats.css';

const UserStats = () => {
  const web3Context = useWeb3();
  const { account, contract, web3 } = web3Context;

  const [stats, setStats] = useState(null);
  const [tradeStats, setTradeStats] = useState(null);
  const [sponsor, setSponsor] = useState(null);
  const [levelRewards, setLevelRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedAccount, setCopiedAccount] = useState(false);

  useEffect(() => {
    if (account && contract) {
      loadUserStats();
    }
  }, [account, contract]);

  const loadUserStats = async () => {
    setLoading(true);
    try {
      // Get user stats
      const userStats = await contract.methods.getUserStats(account).call();
      setStats({
        tradeBonus: web3.utils.fromWei(userStats.tradeBonus.toString(), 'ether'),
        referralBonus: web3.utils.fromWei(userStats.referralBonus.toString(), 'ether'),
        stakeBonus: web3.utils.fromWei(userStats.stakeBonus.toString(), 'ether'),
        sponsorUSDTBonus: web3.utils.fromWei(userStats.sponsorUSDTBonus.toString(), 'ether'),
        refs: userStats.refs.toString(),
        levelReward: web3.utils.fromWei(userStats.levelReward.toString(), 'ether')
      });

      // Get trade stats
      const tradeInfo = await contract.methods.getTradeStats(account).call();
      setTradeStats({
        activeRefs: tradeInfo.activeRefs.toString(),
        buyCount: tradeInfo.buyCount.toString(),
        sellCount: tradeInfo.sellCount.toString()
      });

      // Get sponsor
      const sponsorAddress = await contract.methods.sponsorOf(account).call();
      setSponsor(sponsorAddress !== '0x0000000000000000000000000000000000000000' ? sponsorAddress : null);

      // Get level reward history
      const rewardCount = await contract.methods.getLevelRewardCount(account).call();
      const rewards = [];
      const count = parseInt(rewardCount.toString());
      for (let i = 0; i < Math.min(count, 15); i++) {
        const reward = await contract.methods.getLevelRewardHistory(account, i).call();
        rewards.push({
          fromUser: reward.fromUser,
          amount: web3.utils.fromWei(reward.amount.toString(), 'ether'),
          level: reward.level.toString(),
          time: new Date(parseInt(reward.time.toString()) * 1000).toLocaleString()
        });
      }
      setLevelRewards(rewards);
    } catch (error) {
      console.error('Error loading user stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(account);
    setCopiedAccount(true);
    setTimeout(() => setCopiedAccount(false), 2000);
  };

  if (!account) {
    return (
      <div className="stats-wrapper">
        <div className="empty-stats-card">
          <div className="icon">📊</div>
          <h2>Wallet Disconnected</h2>
          <p>Please connect your Web3 wallet to inspect your trading and referral statistics.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="stats-wrapper">
        <div className="empty-stats-card">
          <div className="spinner-loader"></div>
          <h2>Loading Statistics...</h2>
        </div>
      </div>
    );
  }

  const totalTokensEarned = (
    parseFloat(stats?.tradeBonus || 0) +
    parseFloat(stats?.referralBonus || 0) +
    parseFloat(stats?.levelReward || 0)
  ).toFixed(2);

  const totalUSDTEarned = (
    parseFloat(stats?.stakeBonus || 0) +
    parseFloat(stats?.sponsorUSDTBonus || 0)
  ).toFixed(2);

  return (
    <div className="stats-wrapper">
      {/* Banner */}
      <div className="stats-banner">
        <div className="banner-left">
          <h2>Trader Dashboard & Analytics</h2>
          <p>Real-time telemetry on bonuses, trading activity, upline tree, and level rewards.</p>
        </div>
        <button className="btn-refresh-stats" onClick={loadUserStats}>
          🔄 Refresh Telemetry
        </button>
      </div>

      {/* Aggregate KPI Summary Cards */}
      <div className="kpi-grid">
        <div className="kpi-card token-kpi">
          <span className="kpi-title">Total Tokens Earned</span>
          <span className="kpi-value">{parseFloat(totalTokensEarned).toLocaleString()} Tokens</span>
          <span className="kpi-sub">Trade + Referral + Level Rewards</span>
        </div>

        <div className="kpi-card usdt-kpi">
          <span className="kpi-title">Total USDT Earned</span>
          <span className="kpi-value">{parseFloat(totalUSDTEarned).toLocaleString()} USDT</span>
          <span className="kpi-sub">Stake Bonuses + Sponsor USDT</span>
        </div>

        <div className="kpi-card ref-kpi">
          <span className="kpi-title">Team Structure</span>
          <span className="kpi-value">{tradeStats?.activeRefs || 0} / {stats?.refs || 0}</span>
          <span className="kpi-sub">Active / Total Referrals</span>
        </div>
      </div>

      {/* Main Section Grid */}
      <div className="stats-grid-main">
        {/* Earnings Breakdown */}
        <div className="stats-box">
          <h3>💰 Reward Breakdowns</h3>
          <div className="stat-rows">
            <div className="s-row">
              <span>Trade Bonus (Buyer Incentive):</span>
              <span className="v-token">{parseFloat(stats?.tradeBonus || 0).toLocaleString()} Tokens</span>
            </div>
            <div className="s-row">
              <span>Buy Order Referral Bonus:</span>
              <span className="v-token">{parseFloat(stats?.referralBonus || 0).toLocaleString()} Tokens</span>
            </div>
            <div className="s-row">
              <span>Stake Unlocked Bonus:</span>
              <span className="v-usdt">{parseFloat(stats?.stakeBonus || 0).toLocaleString()} USDT</span>
            </div>
            <div className="s-row">
              <span>Sponsor Stake USDT Bonus:</span>
              <span className="v-usdt">{parseFloat(stats?.sponsorUSDTBonus || 0).toLocaleString()} USDT</span>
            </div>
            <div className="s-row">
              <span>Claim Level Rewards:</span>
              <span className="v-token">{parseFloat(stats?.levelReward || 0).toLocaleString()} Tokens</span>
            </div>
          </div>
        </div>

        {/* Trading Orders Telemetry */}
        <div className="stats-box">
          <h3>📈 Marketplace Telemetry</h3>
          <div className="stat-rows">
            <div className="s-row">
              <span>Total Buy Orders Executed:</span>
              <span className="v-num">{tradeStats?.buyCount || 0} Orders</span>
            </div>
            <div className="s-row">
              <span>Total Sell Orders Created:</span>
              <span className="v-num">{tradeStats?.sellCount || 0} Orders</span>
            </div>
            <div className="s-row">
              <span>Direct Referrals:</span>
              <span className="v-num">{stats?.refs || 0} Members</span>
            </div>
            <div className="s-row">
              <span>Active Referral Status:</span>
              <span className={`v-status ${parseInt(tradeStats?.activeRefs || 0) > 0 ? 'active' : 'inactive'}`}>
                {parseInt(tradeStats?.activeRefs || 0) > 0 ? '✓ Unlocked Unlimited Sells' : '⚠️ 1 Sell Order Limit'}
              </span>
            </div>
          </div>
        </div>

        {/* Sponsor & Wallet Info */}
        <div className="stats-box full-box">
          <h3>🔗 Network Node & Sponsor</h3>
          <div className="node-grid">
            <div className="node-info">
              <span className="label">Your Upline Sponsor:</span>
              <span className="val address-font">
                {sponsor ? sponsor : 'Direct Protocol Participant (No Sponsor)'}
              </span>
            </div>
            <div className="node-info">
              <span className="label">Your Connected Account Node:</span>
              <div className="account-copy-wrapper">
                <input type="text" readOnly value={account} />
                <button onClick={copyAddress}>
                  {copiedAccount ? '✓ Copied' : '📋 Copy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Level Reward History Table */}
      {levelRewards.length > 0 && (
        <div className="history-box">
          <h3>📜 Recent Level Reward Payouts</h3>
          <div className="history-table-wrapper">
            <table className="history-table">
              <thead>
                <tr>
                  <th>From Trader</th>
                  <th>Level</th>
                  <th>Bonus Amount</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {levelRewards.map((reward, index) => (
                  <tr key={index}>
                    <td className="address-font">{reward.fromUser.slice(0, 8)}...{reward.fromUser.slice(-6)}</td>
                    <td><span className="level-badge">Level {reward.level}</span></td>
                    <td className="token-txt">+{parseFloat(reward.amount).toLocaleString()} Tokens</td>
                    <td className="time-txt">{reward.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserStats;